"use client";

/**
 * ReelsToBuy - Instagram/TikTok-style vertical reels/carousel feed with purchase limit
 *
 * Features:
 * - Supports both images and videos
 * - Limit of 3 items to view before showing "Buy" button
 * - For images: TikTok-style horizontal carousel with left/right arrows
 * - For videos: Vertical scroll (swipe up/down)
 * - After viewing 3 items, shows purchase CTA
 */

import MuxPlayer from "@mux/mux-player-react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Play,
  ShoppingCart,
  Volume2,
  VolumeX,
} from "lucide-react";
import {
  isPriceValid,
  trackMuxCheckoutClick,
  trackMuxCtaViewed,
  trackMuxLimitReached,
  trackMuxPaywallFailure,
  type MuxPaywallSurface,
} from "@/lib/muxPaywallFunnelTracking";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Hook to detect mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// Hook to detect iOS
const useIsIOS = () => {
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice =
      /iphone|ipad|ipod/.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
  }, []);

  return isIOS;
};

export interface MediaItem {
  type: "image" | "video";
  src: string; // For images: URL, For videos: Mux playbackId o URL completa stream.mux.com/xxx.m3u8
  alt?: string; // For images
  thumbnail?: string; // Optional thumbnail for videos
}

/** Normaliza video_url: acepta Playback ID o URL completa; devuelve Playback ID para MuxPlayer */
export function toMuxPlaybackId(videoUrl: string): string {
  if (!videoUrl?.trim()) return videoUrl;
  const m = videoUrl.match(/stream\.mux\.com\/([a-zA-Z0-9]+)(?:\.m3u8)?/);
  return m ? m[1] : videoUrl.trim();
}

interface ReelsToBuyProps {
  items: MediaItem[];
  maxFreeItems?: number; // Default: 3
  purchaseUrl?: string; // URL to redirect when clicking "Buy"
  onPurchaseClick?: () => void; // Callback when clicking "Buy"
  price?: string; // Price to display (e.g., "$9.99")
  packName?: string; // Name of the pack (e.g., "Pack Premium")
  
  // Nuevos: Personalización desde el dashboard
  buttonText?: string; // Texto del botón (default: "Comprar Ahora")
  buttonColor?: string; // Color del botón en hex (default: "#ec4899")
  heroImage?: string; // Imagen para la pantalla de compra
  description?: string; // Descripción del producto
  /** PostHog: superficie del embudo mux paywall */
  analyticsSurface?: MuxPaywallSurface;
  productId?: string;
  productSlug?: string;
  priceAmount?: number;
  priceCurrency?: string;
}

const ReelsToBuy: React.FC<ReelsToBuyProps> = ({
  items,
  maxFreeItems = 3,
  purchaseUrl,
  onPurchaseClick,
  price = "$9.99",
  packName = "Pack Premium",
  buttonText = "Comprar Ahora",
  buttonColor = "#ec4899",
  heroImage,
  description,
  analyticsSurface = "reels_to_buy",
  productId,
  productSlug,
  priceAmount,
  priceCurrency,
}) => {
  // Core state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewedCount, setViewedCount] = useState(1); // Start at 1 (viewing first item)
  const [showPurchaseScreen, setShowPurchaseScreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isBuffering, setIsBuffering] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Touch/swipe state
  const [touchStart, setTouchStart] = useState<{
    x: number;
    y: number;
    time: number;
  } | null>(null);
  const [swipeOffset, setSwipeOffset] = useState({ x: 0, y: 0 });
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();

  // Get current item
  const currentItem = items[currentIndex];
  const isVideo = currentItem?.type === "video";
  const isImage = currentItem?.type === "image";

  const currentPlaybackId = useMemo(() => {
    if (!isVideo || !currentItem?.src) return undefined;
    return toMuxPlaybackId(currentItem.src);
  }, [isVideo, currentItem?.src]);

  const paywallAnalyticsBase = useCallback(
    () => ({
      surface: analyticsSurface,
      playbackId: currentPlaybackId,
      videoTitle: currentItem?.alt ?? packName,
      priceShown: price,
      priceAmount,
      priceCurrency,
      limitReachedCount: viewedCount,
      maxFreeItems,
      productId,
      productSlug,
      packName,
      videoIndex: currentIndex,
    }),
    [
      analyticsSurface,
      currentPlaybackId,
      currentItem?.alt,
      packName,
      price,
      priceAmount,
      priceCurrency,
      viewedCount,
      maxFreeItems,
      productId,
      productSlug,
      currentIndex,
    ],
  );

  // Check if reached limit
  const hasReachedLimit = viewedCount >= maxFreeItems;

  const inlineCtaTrackedRef = useRef(false);
  const purchaseScreenTrackedRef = useRef(false);
  const mountDiagTrackedRef = useRef(false);
  const limitReachedTrackedRef = useRef(false);

  useEffect(() => {
    if (mountDiagTrackedRef.current) return;
    mountDiagTrackedRef.current = true;
    const base = {
      ...paywallAnalyticsBase(),
      trigger: "page_mount" as const,
      itemsCount: items.length,
      componentName: "ReelsToBuy",
    };
    if (items.length === 0) {
      trackMuxPaywallFailure({
        ...base,
        failureType: "playlist_empty",
        failureReason: "reels_items_empty",
      });
      return;
    }
    if (items.length < maxFreeItems) {
      trackMuxPaywallFailure({
        ...base,
        failureType: "playlist_too_short",
        failureReason: `items_${items.length}_lt_max_${maxFreeItems}`,
      });
    }
    const validPrice = isPriceValid(price, priceAmount);
    if (!validPrice) {
      trackMuxPaywallFailure({
        ...base,
        failureType: price?.trim() ? "price_invalid" : "price_missing",
        priceValid: false,
      });
    }
  }, [items.length, maxFreeItems, paywallAnalyticsBase, price, priceAmount]);

  useEffect(() => {
    if (!hasReachedLimit) return;
    if (limitReachedTrackedRef.current) return;
    limitReachedTrackedRef.current = true;
    trackMuxLimitReached({
      ...paywallAnalyticsBase(),
      trigger: showPurchaseScreen ? "limit_reached_screen" : "inline_bottom_cta",
    });
  }, [hasReachedLimit, showPurchaseScreen, paywallAnalyticsBase]);

  useEffect(() => {
    if (!hasReachedLimit || showPurchaseScreen) return;
    if (inlineCtaTrackedRef.current) return;
    inlineCtaTrackedRef.current = true;
    const validPrice = isPriceValid(price, priceAmount);
    trackMuxCtaViewed({
      ...paywallAnalyticsBase(),
      trigger: "inline_bottom_cta",
      priceValid: validPrice,
      ...(!validPrice
        ? {
            failureType: price?.trim() ? "price_invalid" : "price_missing",
          }
        : {}),
      extra: { cta_placement: "inline_bottom" },
    });
  }, [hasReachedLimit, showPurchaseScreen, paywallAnalyticsBase, price, priceAmount]);

  useEffect(() => {
    if (!showPurchaseScreen) {
      purchaseScreenTrackedRef.current = false;
      return;
    }
    if (purchaseScreenTrackedRef.current) return;
    purchaseScreenTrackedRef.current = true;
    const validPrice = isPriceValid(price, priceAmount);
    trackMuxCtaViewed({
      ...paywallAnalyticsBase(),
      trigger: "limit_reached_screen",
      priceValid: validPrice,
      failureType: !validPrice
        ? price?.trim()
          ? "price_invalid"
          : "price_missing"
        : undefined,
      extra: { cta_placement: "full_screen_purchase" },
    });
  }, [showPurchaseScreen, paywallAnalyticsBase, price, priceAmount]);

  // Update document title
  useEffect(() => {
    if (showPurchaseScreen) {
      document.title = "Comprar Pack";
    } else if (items.length > 0) {
      document.title = `${currentIndex + 1}/${Math.min(maxFreeItems, items.length)}`;
    }
  }, [currentIndex, showPurchaseScreen, items.length, maxFreeItems]);

  // Reset video state when changing items
  useEffect(() => {
    if (isVideo) {
      setIsBuffering(true);
      setHasError(false);
      setProgress(0);
      setIsPlaying(true);
    }
  }, [currentIndex, isVideo]);

  // Navigate to a specific item
  const goToItem = useCallback(
    (index: number) => {
      if (isTransitioning) return;

      // Check if trying to go beyond free limit
      if (index >= maxFreeItems) {
        setShowPurchaseScreen(true);
        return;
      }

      if (index < 0 || index >= items.length) return;

      setIsTransitioning(true);

      // Pause current video if playing
      if (isVideo && playerRef.current?.pause) {
        playerRef.current.pause();
      }

      // Update index
      setCurrentIndex(index);

      // Update viewed count (track the highest index reached)
      setViewedCount((prev) => Math.max(prev, index + 1));

      setTimeout(() => {
        setIsTransitioning(false);
      }, 200);
    },
    [items.length, isTransitioning, isVideo, maxFreeItems]
  );

  const goToNext = useCallback(() => {
    if (showPurchaseScreen) return;

    // For videos: vertical navigation
    if (isVideo) {
      if (currentIndex + 1 >= maxFreeItems) {
        setShowPurchaseScreen(true);
      } else {
        goToItem(currentIndex + 1);
      }
    }
    // For images: horizontal navigation
    else if (isImage) {
      if (currentIndex + 1 >= maxFreeItems) {
        setShowPurchaseScreen(true);
      } else {
        goToItem(currentIndex + 1);
      }
    }
  }, [currentIndex, goToItem, showPurchaseScreen, isVideo, isImage, maxFreeItems]);

  const goToPrevious = useCallback(() => {
    if (showPurchaseScreen) {
      setShowPurchaseScreen(false);
      return;
    }

    if (currentIndex > 0) {
      goToItem(currentIndex - 1);
    }
  }, [currentIndex, goToItem, showPurchaseScreen]);

  // Touch handlers for videos (vertical swipe)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY, time: Date.now() });
    setSwipeOffset({ x: 0, y: 0 });
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart) return;
      const touch = e.touches[0];

      if (isVideo) {
        // Vertical swipe for videos
        const deltaY = touchStart.y - touch.clientY;
        const resistance = 0.4;
        const maxOffset = 150;
        const offset =
          Math.sign(deltaY) *
          Math.min(Math.abs(deltaY) * resistance, maxOffset);
        setSwipeOffset({ x: 0, y: offset });
      } else if (isImage) {
        // Horizontal swipe for images
        const deltaX = touchStart.x - touch.clientX;
        const resistance = 0.5;
        const maxOffset = 200;
        const offset =
          Math.sign(deltaX) *
          Math.min(Math.abs(deltaX) * resistance, maxOffset);
        setSwipeOffset({ x: offset, y: 0 });
      }
    },
    [touchStart, isVideo, isImage]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart) return;

      const touch = e.changedTouches[0];
      const deltaX = touchStart.x - touch.clientX;
      const deltaY = touchStart.y - touch.clientY;
      const deltaTime = Date.now() - touchStart.time;

      setSwipeOffset({ x: 0, y: 0 });

      if (isVideo) {
        // Vertical swipe for videos
        const velocity = Math.abs(deltaY) / deltaTime;
        const swipeThreshold = isIOS ? 40 : 50;
        const velocityThreshold = isIOS ? 0.25 : 0.3;
        const isSwipe =
          Math.abs(deltaY) > swipeThreshold || velocity > velocityThreshold;

        if (isSwipe) {
          if (deltaY > 0) {
            goToNext();
          } else {
            goToPrevious();
          }
        }
      } else if (isImage) {
        // Horizontal swipe for images
        const velocity = Math.abs(deltaX) / deltaTime;
        const swipeThreshold = isIOS ? 40 : 50;
        const velocityThreshold = isIOS ? 0.25 : 0.3;
        const isSwipe =
          Math.abs(deltaX) > swipeThreshold || velocity > velocityThreshold;

        if (isSwipe) {
          if (deltaX > 0) {
            goToNext();
          } else {
            goToPrevious();
          }
        }
      }

      setTouchStart(null);
    },
    [touchStart, goToNext, goToPrevious, isIOS, isVideo, isImage]
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPurchaseScreen) return;

      if (isVideo) {
        // Vertical navigation for videos
        if (e.key === "ArrowDown" || e.key === "j") {
          e.preventDefault();
          goToNext();
        } else if (e.key === "ArrowUp" || e.key === "k") {
          e.preventDefault();
          goToPrevious();
        }
      } else if (isImage) {
        // Horizontal navigation for images
        if (e.key === "ArrowRight" || e.key === "l") {
          e.preventDefault();
          goToNext();
        } else if (e.key === "ArrowLeft" || e.key === "h") {
          e.preventDefault();
          goToPrevious();
        }
      }

      // Common controls
      if (e.key === "m" && isVideo) {
        setIsMuted((prev) => !prev);
      } else if (e.key === " ") {
        e.preventDefault();
        if (isVideo && playerRef.current) {
          if (playerRef.current.paused) {
            playerRef.current.play();
            setIsPlaying(true);
          } else {
            playerRef.current.pause();
            setIsPlaying(false);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToNext, goToPrevious, showPurchaseScreen, isVideo, isImage]);

  // Wheel navigation (desktop)
  useEffect(() => {
    if (isMobile || !isVideo) return;

    let lastWheelTime = 0;
    const wheelThrottle = 500;

    const handleWheel = (e: WheelEvent) => {
      const now = Date.now();
      if (now - lastWheelTime < wheelThrottle) return;

      if (Math.abs(e.deltaY) > 50) {
        lastWheelTime = now;
        if (e.deltaY > 0) {
          goToNext();
        } else {
          goToPrevious();
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: true });
      return () => container.removeEventListener("wheel", handleWheel);
    }
  }, [isMobile, goToNext, goToPrevious, isVideo]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (isVideo && playerRef.current) {
      if (playerRef.current.paused) {
        playerRef.current.play();
        setIsPlaying(true);
      } else {
        playerRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isVideo]);

  const handleVideoTap = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if ("touches" in e) return;
      togglePlayPause();
    },
    [togglePlayPause]
  );

  // Video event handlers
  const handleCanPlay = useCallback(() => {
    setIsBuffering(false);
    if (playerRef.current && playerRef.current.paused) {
      playerRef.current.play().catch(() => {});
    }
  }, []);

  const handleEnded = useCallback(() => {
    goToNext();
  }, [goToNext]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setIsBuffering(false);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleWaiting = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handlePlaying = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleError = useCallback(() => {
    setIsBuffering(false);
    setHasError(true);
    trackMuxPaywallFailure({
      ...paywallAnalyticsBase(),
      failureType: "video_playback_failure",
      failureReason: "mux_player_error",
      playbackId: currentPlaybackId,
    });
  }, [paywallAnalyticsBase, currentPlaybackId]);

  const handleTimeUpdate = useCallback((e: any) => {
    const video = e.target;
    if (video && video.duration) {
      const prog = (video.currentTime / video.duration) * 100;
      setProgress(prog);
    }
  }, []);

  const retryVideo = useCallback(() => {
    setHasError(false);
    setIsBuffering(true);
    const idx = currentIndex;
    setCurrentIndex(-1);
    setTimeout(() => setCurrentIndex(idx), 50);
  }, [currentIndex]);

  // Handle purchase button click
  const handlePurchase = useCallback(() => {
    const ctx = paywallAnalyticsBase();
    const validPrice = isPriceValid(price, priceAmount);
    trackMuxCheckoutClick({
      ...ctx,
      trigger: showPurchaseScreen ? "limit_reached_screen" : "inline_bottom_cta",
      priceValid: validPrice,
      failureType: !validPrice
        ? price?.trim()
          ? "price_invalid"
          : "price_missing"
        : undefined,
    });
    if (!onPurchaseClick && !purchaseUrl) {
      trackMuxPaywallFailure({
        ...ctx,
        failureType: "checkout_handler_missing",
        failureReason: "no_onPurchaseClick_nor_purchaseUrl",
      });
      return;
    }
    if (onPurchaseClick) {
      onPurchaseClick();
    }
    if (purchaseUrl) {
      window.location.href = purchaseUrl;
    }
  }, [
    onPurchaseClick,
    purchaseUrl,
    paywallAnalyticsBase,
    price,
    priceAmount,
    showPurchaseScreen,
  ]);

  // Styles
  const componentStyles = useMemo(
    () => `
    .reels-to-buy-container {
      width: 100%;
      height: 100dvh;
      height: 100vh;
      overflow: hidden;
      position: fixed;
      inset: 0;
      background: #000;
      touch-action: ${isVideo ? "pan-y" : "pan-x"};
      -webkit-overflow-scrolling: touch;
    }

    .reels-media-wrapper {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #000;
    }

    .reels-mux-player {
      width: 100% !important;
      height: 100% !important;
      max-width: 100vw !important;
      max-height: 100dvh !important;
      max-height: 100vh !important;
      --controls: none !important;
      --media-object-fit: cover !important;
    }

    .reels-mux-player video {
      object-fit: cover !important;
      width: 100% !important;
      height: 100% !important;
    }

    .reels-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      user-select: none;
      -webkit-user-drag: none;
    }

    @media (min-width: 768px) {
      .reels-mux-player {
        max-width: min(56.25vh, 100vw) !important;
        --media-object-fit: contain !important;
      }
      .reels-mux-player video {
        object-fit: contain !important;
      }
    }

    @supports (-webkit-touch-callout: none) {
      .reels-to-buy-container {
        height: -webkit-fill-available;
      }
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .animate-fade-in-up {
      animation: fadeInUp 0.5s ease-out forwards;
    }

    .progress-bar {
      transition: width 0.1s linear;
    }

    @keyframes pulse-ring {
      0% { transform: scale(0.8); opacity: 1; }
      100% { transform: scale(1.3); opacity: 0; }
    }

    .buffering-ring {
      animation: pulse-ring 1s ease-out infinite;
    }

    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(100px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-100px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    .slide-in-right {
      animation: slideInRight 0.3s ease-out forwards;
    }

    .slide-in-left {
      animation: slideInLeft 0.3s ease-out forwards;
    }
  `,
    [isVideo]
  );

  // No items
  if (items.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center px-8">
          <p className="text-white/60 text-sm mb-4">No hay contenido disponible</p>
          <a
            href="/"
            className="text-white/40 hover:text-white/60 text-xs transition-colors"
          >
            ← Volver
          </a>
        </div>
      </div>
    );
  }

  // Purchase screen
  if (showPurchaseScreen) {
    return (
      <>
        <style>{componentStyles}</style>
        <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden">
          <div className="relative z-10 text-center px-8 max-w-md">
            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "0ms" }}
            >
              {/* Hero image personalizada o icono por defecto */}
              {heroImage ? (
                <img 
                  src={heroImage} 
                  alt={packName}
                  className="w-32 h-32 mx-auto mb-6 rounded-2xl object-cover shadow-2xl"
                />
              ) : (
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                  <ShoppingCart className="w-10 h-10 text-white" />
                </div>
              )}

              {/* Nombre del producto */}
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {packName}
              </h1>

              {/* Descripción (opcional, viene de la API) */}
              {description && (
                <p className="text-white/70 text-base mb-6">
                  {description}
                </p>
              )}

              {/* Precio (viene de la API) */}
              <div className="mb-8">
                <div className="text-4xl font-bold text-white">
                  {price}
                </div>
              </div>
            </div>

            <div
              className="animate-fade-in-up space-y-3"
              style={{ animationDelay: "200ms" }}
            >
              {/* Botón con color personalizado */}
              <button
                type="button"
                onClick={handlePurchase}
                data-ph-capture-attribute-mux-cta="purchase_screen"
                style={{
                  background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                }}
                className="w-full min-h-[3rem] py-4 text-base text-white font-semibold rounded-xl ui-transition transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl ring-2 ring-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {buttonText}
              </button>

              <button
                onClick={() => setShowPurchaseScreen(false)}
                className="w-full py-3 text-white/40 hover:text-white/60 text-sm transition-colors"
              >
                ← Ver preview de nuevo
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{componentStyles}</style>

      <div
        ref={containerRef}
        className="reels-to-buy-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform:
            swipeOffset.x !== 0
              ? `translateX(${-swipeOffset.x}px)`
              : swipeOffset.y !== 0
              ? `translateY(${-swipeOffset.y}px)`
              : undefined,
          transition:
            swipeOffset.x === 0 && swipeOffset.y === 0
              ? "transform 0.2s ease-out"
              : "none",
        }}
      >
        {/* Media content */}
        {currentItem && currentIndex >= 0 && (
          <div className="reels-media-wrapper">
            {/* VIDEO */}
            {isVideo && (
              <>
                {/* Buffering indicator */}
                {isBuffering && !hasError && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full border-2 border-white/30 buffering-ring" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full border-2 border-white/50 border-t-transparent animate-spin" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Error overlay */}
                {hasError && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/90">
                    <div className="text-center px-8">
                      <p className="text-white/70 text-sm mb-4">
                        No se pudo cargar el video
                      </p>
                      <div className="flex flex-col gap-3">
                        <button
                          onClick={retryVideo}
                          className="px-5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full text-sm transition-colors"
                        >
                          Reintentar
                        </button>
                        {currentIndex < maxFreeItems - 1 && (
                          <button
                            onClick={goToNext}
                            className="text-white/40 hover:text-white/60 text-xs"
                          >
                            Siguiente
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pause overlay */}
                {!isPlaying && !isBuffering && !hasError && (
                  <div
                    className="absolute inset-0 z-15 flex items-center justify-center pointer-events-none"
                    onClick={handleVideoTap}
                  >
                    <div className="w-16 h-16 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                      <Play className="w-8 h-8 text-white ml-1" fill="white" />
                    </div>
                  </div>
                )}

                <MuxPlayer
                  key={`video-${currentIndex}`}
                  ref={playerRef}
                  streamType="on-demand"
                  playbackId={toMuxPlaybackId(currentItem.src)}
                  autoPlay
                  muted={isMuted}
                  playsInline
                  preload="auto"
                  disableCookies
                  storyboardSrc=""
                  thumbnailTime={0}
                  startTime={0}
                  onCanPlay={handleCanPlay}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onWaiting={handleWaiting}
                  onPlaying={handlePlaying}
                  onEnded={handleEnded}
                  onError={handleError}
                  onTimeUpdate={handleTimeUpdate}
                  className="reels-mux-player"
                  style={{ width: "100%", height: "100%" }}
                />
              </>
            )}

            {/* IMAGE */}
            {isImage && (
              <img
                key={`image-${currentIndex}`}
                src={currentItem.src}
                alt={currentItem.alt || `Imagen ${currentIndex + 1}`}
                className="reels-image"
              />
            )}
          </div>
        )}

        {/* UI overlay */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {/* Progress bar (for videos) */}
          {isVideo && (
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10">
              <div
                className="h-full bg-white/80 progress-bar"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Top controls */}
          <div className="absolute top-3 left-0 right-0 px-4 flex items-center justify-between">
            {/* Counter */}
            <div className="px-2.5 py-1 bg-black/30 backdrop-blur-sm rounded-full text-white/80 text-xs font-medium">
              {currentIndex + 1}/{maxFreeItems} preview
            </div>

            {/* Mute button (videos only) */}
            {isVideo && (
              <button
                onClick={toggleMute}
                className="w-9 h-9 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white/80 pointer-events-auto active:scale-95 transition-transform"
                aria-label={isMuted ? "Activar sonido" : "Silenciar"}
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </button>
            )}
          </div>

          {/* Navigation arrows */}
          {isVideo ? (
            // Vertical arrows for videos (desktop only)
            !isMobile && (
              <>
                {currentIndex > 0 && (
                  <button
                    onClick={goToPrevious}
                    className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 -mt-20 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white/60 hover:text-white pointer-events-auto ui-transition"
                    aria-label="Anterior"
                  >
                    <ChevronUp className="w-5 h-5" />
                  </button>
                )}
                {currentIndex < maxFreeItems - 1 && (
                  <button
                    onClick={goToNext}
                    className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 mt-20 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white/60 hover:text-white pointer-events-auto ui-transition"
                    aria-label="Siguiente"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                )}
              </>
            )
          ) : (
            // Horizontal arrows for images (always visible)
            <>
              {currentIndex > 0 && (
                <button
                  onClick={goToPrevious}
                  className="absolute top-1/2 -translate-y-1/2 left-4 w-12 h-12 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white/80 hover:text-white pointer-events-auto ui-transition"
                  aria-label="Anterior"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              {currentIndex < maxFreeItems - 1 ? (
                <button
                  onClick={goToNext}
                  className="absolute top-1/2 -translate-y-1/2 right-4 w-12 h-12 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white/80 hover:text-white pointer-events-auto ui-transition"
                  aria-label="Siguiente"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              ) : (
                // Show "Buy" button on the right after viewing 3 images
                <button
                  onClick={() => setShowPurchaseScreen(true)}
                  style={{
                    background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`
                  }}
                  className="absolute top-1/2 -translate-y-1/2 right-4 px-6 py-3 backdrop-blur-sm rounded-full flex items-center gap-2 text-white font-semibold pointer-events-auto ui-transition transform hover:scale-105 active:scale-95 shadow-lg"
                  aria-label="Comprar"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span>{buttonText}</span>
                </button>
              )}
            </>
          )}

          {/* Bottom CTA for videos after viewing 3 */}
          {isVideo && hasReachedLimit && !showPurchaseScreen && (
            <div className="absolute inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(2rem,calc(env(safe-area-inset-bottom,0px)+1.5rem))] pt-16 pointer-events-none bg-gradient-to-t from-black/85 via-black/50 to-transparent">
              <button
                type="button"
                onClick={() => setShowPurchaseScreen(true)}
                data-ph-capture-attribute-mux-cta="inline_limit"
                style={{
                  background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                }}
                className="pointer-events-auto min-h-[3rem] px-8 py-4 rounded-full flex items-center gap-3 text-white text-base font-semibold ui-transition transform hover:scale-[1.02] active:scale-[0.98] shadow-2xl ring-2 ring-white/25 animate-fade-in-up focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                aria-label={buttonText}
              >
                <ShoppingCart className="w-5 h-5 shrink-0" aria-hidden />
                <span>{buttonText}</span>
              </button>
            </div>
          )}

          {/* Swipe hint (first item on mobile) */}
          {isMobile && currentIndex === 0 && items.length > 1 && (
            <div
              className={`absolute ${
                isVideo
                  ? "bottom-24 left-1/2 -translate-x-1/2"
                  : "top-1/2 -translate-y-1/2 right-8"
              } animate-bounce pointer-events-none`}
            >
              {isVideo ? (
                <ChevronUp className="w-6 h-6 text-white/40" />
              ) : (
                <ChevronRight className="w-6 h-6 text-white/40" />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReelsToBuy;
